// ADDED: Composable for website onboarding wizard state management
// PRD Reference: Dashboard Redesign + Website Onboarding Wizard plan
import { ref, computed, reactive } from 'vue'
import * as api from '~/javascript/api/onboarding'

const STEPS = {
  1: 'business_basics',
  2: 'services',
  3: 'service_areas',
  4: 'gallery'
}

const STEP_TITLES = {
  1: 'Business Basics',
  2: 'Services',
  3: 'Service Areas',
  4: 'Photo Gallery'
}

export default function useWizard(websiteId = null, initialStep = 1) {
  // Store websiteId for all API calls
  const currentWebsiteId = ref(websiteId)
  // State
  const currentStep = ref(initialStep)
  const loading = ref(true) // Start true to prevent flash before initialize()
  const saving = ref(false)
  const error = ref(null)

  // Website data
  const website = reactive({
    id: null,
    business_name: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    logo_url: null,
    industry: '',
    industry_display_name: '',
    industry_description: '',
    wizard_status: 'not_started',
    wizard_current_step: 1,
    wizard_completed_steps: []
  })

  // Related data
  const services = ref([])
  const serviceAreas = ref([])
  const galleryImages = ref([])

  // Computed
  const totalSteps = computed(() => Object.keys(STEPS).length)

  const currentStepName = computed(() => STEPS[currentStep.value])

  const currentStepTitle = computed(() => STEP_TITLES[currentStep.value])

  const isFirstStep = computed(() => currentStep.value === 1)

  const isLastStep = computed(() => currentStep.value === totalSteps.value)

  const progressPercentage = computed(() => {
    const completedSteps = website.wizard_completed_steps || []
    return Math.round((completedSteps.length / totalSteps.value) * 100)
  })

  const isStepCompleted = (step) => (website.wizard_completed_steps || []).includes(step)

  const canProceed = computed(() => {
    // Each step has its own validation
    switch (currentStep.value) {
      case 1: // Business basics - need at least a business name
        return website.business_name?.trim().length > 0
      case 2: // Services - need at least one service
        return services.value.length > 0
      case 3: // Service areas - need at least one area
        return serviceAreas.value.length > 0
      case 4: // Gallery - optional, can always proceed
        return true
      default:
        return false
    }
  })

  // Actions
  async function loadWebsite() {
    loading.value = true
    error.value = null

    try {
      const response = await api.getWebsite(currentWebsiteId.value)
      // Ensure wizard_completed_steps is always an array
      const data = {
        ...response.data,
        wizard_completed_steps: response.data.wizard_completed_steps || []
      }
      Object.assign(website, data)
      // Update currentWebsiteId if we got a website back
      if (data.id) {
        currentWebsiteId.value = data.id
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load website data'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function saveWebsite(data) {
    saving.value = true
    error.value = null

    try {
      const response = await api.updateWebsite(data, currentWebsiteId.value)
      Object.assign(website, response.data)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to save website'
      throw err
    } finally {
      saving.value = false
    }
  }

  async function uploadLogo(file, onProgress) {
    const formData = new FormData()
    formData.append('logo', file)

    const opts = onProgress ? {
      onUploadProgress: (e) => onProgress(Math.round((e.loaded / e.total) * 100))
    } : {}

    try {
      const response = await api.uploadLogo(formData, opts, currentWebsiteId.value)
      website.logo_url = response.data.logo_url
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to upload logo'
      throw err
    }
  }

  async function removeLogo() {
    try {
      await api.removeLogo(currentWebsiteId.value)
      website.logo_url = null
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove logo'
      throw err
    }
  }

  async function completeCurrentStep() {
    saving.value = true
    error.value = null

    try {
      const response = await api.completeStep(currentStep.value, currentWebsiteId.value)
      website.wizard_completed_steps = response.data.wizard_completed_steps
      website.wizard_status = response.data.wizard_status
      website.wizard_current_step = response.data.wizard_current_step
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to complete step'
      throw err
    } finally {
      saving.value = false
    }
  }

  // Services
  async function loadServices() {
    try {
      const response = await api.getServices(currentWebsiteId.value)
      services.value = response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load services'
      throw err
    }
  }

  async function addService(data) {
    try {
      const response = await api.createService(data, currentWebsiteId.value)
      services.value.push(response.data)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to add service'
      throw err
    }
  }

  async function updateService(id, data) {
    try {
      const response = await api.updateService(id, data, currentWebsiteId.value)
      const index = services.value.findIndex((s) => s.id === id)
      if (index !== -1) {
        services.value[index] = response.data
      }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update service'
      throw err
    }
  }

  async function removeService(id) {
    try {
      await api.deleteService(id, currentWebsiteId.value)
      services.value = services.value.filter((s) => s.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove service'
      throw err
    }
  }

  async function reorderServices(ids) {
    try {
      await api.reorderServices(ids, currentWebsiteId.value)
      // Re-sort local array based on new order
      const orderMap = new Map(ids.map((id, i) => [id, i]))
      services.value.sort((a, b) => orderMap.get(a.id) - orderMap.get(b.id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to reorder services'
      throw err
    }
  }

  async function uploadServicePhotos(serviceId, files, onProgress) {
    const formData = new FormData()
    files.forEach((file) => formData.append('photos[]', file))

    const opts = onProgress ? {
      onUploadProgress: (e) => onProgress(Math.round((e.loaded / e.total) * 100))
    } : {}

    try {
      const response = await api.uploadServicePhotos(serviceId, formData, opts, currentWebsiteId.value)
      const index = services.value.findIndex((s) => s.id === serviceId)
      if (index !== -1) {
        services.value[index] = response.data
      }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to upload photos'
      throw err
    }
  }

  async function removeServicePhoto(serviceId, photoId) {
    try {
      const response = await api.removeServicePhoto(serviceId, photoId, currentWebsiteId.value)
      const index = services.value.findIndex((s) => s.id === serviceId)
      if (index !== -1) {
        services.value[index] = response.data
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove photo'
      throw err
    }
  }

  // Service Areas
  async function loadServiceAreas() {
    try {
      const response = await api.getServiceAreas(currentWebsiteId.value)
      serviceAreas.value = response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load service areas'
      throw err
    }
  }

  async function addServiceArea(data) {
    try {
      const response = await api.createServiceArea(data, currentWebsiteId.value)
      serviceAreas.value.push(response.data)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to add service area'
      throw err
    }
  }

  async function removeServiceArea(id) {
    try {
      await api.deleteServiceArea(id, currentWebsiteId.value)
      serviceAreas.value = serviceAreas.value.filter((a) => a.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove service area'
      throw err
    }
  }

  // Gallery Images
  async function loadGalleryImages() {
    try {
      const response = await api.getGalleryImages(currentWebsiteId.value)
      galleryImages.value = response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load gallery images'
      throw err
    }
  }

  async function uploadGalleryImages(files, category, onProgress) {
    const formData = new FormData()
    files.forEach((file) => formData.append('images[]', file))
    formData.append('category', category)

    const opts = onProgress ? {
      onUploadProgress: (e) => onProgress(Math.round((e.loaded / e.total) * 100))
    } : {}

    try {
      const response = await api.uploadGalleryBatch(formData, opts, currentWebsiteId.value)
      galleryImages.value.push(...response.data)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to upload images'
      throw err
    }
  }

  async function updateGalleryImage(id, data) {
    try {
      const response = await api.updateGalleryImage(id, data, currentWebsiteId.value)
      const index = galleryImages.value.findIndex((i) => i.id === id)
      if (index !== -1) {
        galleryImages.value[index] = response.data
      }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update image'
      throw err
    }
  }

  async function removeGalleryImage(id) {
    try {
      await api.deleteGalleryImage(id, currentWebsiteId.value)
      galleryImages.value = galleryImages.value.filter((i) => i.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove image'
      throw err
    }
  }

  async function reorderGalleryImages(ids) {
    try {
      await api.reorderGalleryImages(ids, currentWebsiteId.value)
      const orderMap = new Map(ids.map((id, i) => [id, i]))
      galleryImages.value.sort((a, b) => orderMap.get(a.id) - orderMap.get(b.id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to reorder images'
      throw err
    }
  }

  // Navigation
  function goToStep(step) {
    if (step >= 1 && step <= totalSteps.value) {
      currentStep.value = step
    }
  }

  async function nextStep() {
    if (!isLastStep.value) {
      await completeCurrentStep()
      currentStep.value++
    }
  }

  function prevStep() {
    if (!isFirstStep.value) {
      currentStep.value--
    }
  }

  // Initialize - load all data for current step
  async function initialize(options = {}) {
    const { manageLoading = true } = options
    loading.value = true

    try {
      await loadWebsite()
      currentStep.value = website.wizard_current_step || 1

      // Pre-load data for all steps
      await Promise.all([
        loadServices(),
        loadServiceAreas(),
        loadGalleryImages()
      ])
    } catch (err) {
      console.error('Failed to initialize wizard:', err)
    } finally {
      // Only set loading=false if caller wants us to manage it
      // Caller can pass { manageLoading: false } to control loading state themselves
      if (manageLoading) {
        loading.value = false
      }
    }
  }

  // Allow caller to manually set loading state
  function setLoading(value) {
    loading.value = value
  }

  return {
    // State
    currentStep,
    loading,
    saving,
    error,
    website,
    services,
    serviceAreas,
    galleryImages,

    // Computed
    totalSteps,
    currentStepName,
    currentStepTitle,
    isFirstStep,
    isLastStep,
    progressPercentage,
    canProceed,
    isStepCompleted,

    // Actions
    initialize,
    loadWebsite,
    saveWebsite,
    uploadLogo,
    removeLogo,
    completeCurrentStep,

    // Services
    loadServices,
    addService,
    updateService,
    removeService,
    reorderServices,
    uploadServicePhotos,
    removeServicePhoto,

    // Service Areas
    loadServiceAreas,
    addServiceArea,
    removeServiceArea,

    // Gallery
    loadGalleryImages,
    uploadGalleryImages,
    updateGalleryImage,
    removeGalleryImage,
    reorderGalleryImages,

    // Navigation
    goToStep,
    nextStep,
    prevStep,

    // Loading control
    setLoading
  }
}
